'use client';

/**
 * Powered by Paylink Footer
 * Reusable footer for the customer journey pages.
 * Displays "Powered by Paylink" alongside Privacy, Terms & Cookie Policy modal links.
 *
 * The policy dialogues themselves live in `LegalPolicyModal`, shared with the
 * consent gate so the wording cannot drift between the two (GitLab #649).
 */

import { useState } from 'react';
import { Sparkles } from 'lucide-react';
import { useConsentSafe } from '@/components/analytics/ConsentProvider';
import {
  LegalPolicyModal,
  type LegalPolicyType,
} from '@/components/legal/LegalPolicyModal';
import type { LegalContentSettings } from '@/types/organization';
import { ReleaseVersion } from '@/components/release/ReleaseVersion';

type ModalType = LegalPolicyType | null;

/** Default legal footer disclaimer, shown when an organisation has not set its own. */
const DEFAULT_FOOTER_TEXT =
  'The Solution service is provided by Nyhavn Limited trading as Paylink. Nyhavn Limited is a limited company registered in England with registered number: 15313717. Registered address: Kempton House Kempton Way, Dysart Road, Grantham, Lincolnshire, NG31 7LE. This website is operated by Nyhavn Limited. Nyhavn Limited is a limited company registered in England with registered number: 15313717. Registered address: Kempton House Kempton Way, Dysart Road, Grantham, Lincolnshire, NG31 7LE.';

interface PoweredByPaylinkFooterProps {
  /** Additional wrapper class names */
  className?: string;
  /** Size variant – compact uses smaller text (e.g. inside chat panels) */
  variant?: 'default' | 'compact';
  /** Organisation-specific legal content. When provided and non-empty, overrides the Paylink defaults. */
  legalContent?: LegalContentSettings;
}

export default function PoweredByPaylinkFooter({
  className = '',
  variant = 'default',
  legalContent,
}: PoweredByPaylinkFooterProps) {
  const [openModal, setOpenModal] = useState<ModalType>(null);
  const consent = useConsentSafe();

  const isCompact = variant === 'compact';
  const textSize = isCompact ? 'text-[10px]' : 'text-xs';
  const iconSize = isCompact ? 'h-2.5 w-2.5' : 'h-3 w-3';

  return (
    <>
      <div className={`flex flex-col items-center gap-1 ${className}`}>
        <div className={`flex items-center justify-center gap-1 text-muted-foreground ${textSize}`}>
          <Sparkles className={iconSize} />
          <span>Powered by Paylink</span>
        </div>
        <div className={`flex items-center gap-3 text-muted-foreground ${textSize}`}>
          <button onClick={() => setOpenModal('privacy')} className="hover:text-foreground transition-colors underline-offset-2 hover:underline">
            Privacy
          </button>
          <span className="text-muted-foreground/40">·</span>
          <button onClick={() => setOpenModal('terms')} className="hover:text-foreground transition-colors underline-offset-2 hover:underline">
            Terms
          </button>
          <span className="text-muted-foreground/40">·</span>
          <button onClick={() => setOpenModal('cookies')} className="hover:text-foreground transition-colors underline-offset-2 hover:underline">
            Cookie Policy
          </button>
          {/* Only rendered where a consent gate exists — admin surfaces have
              no provider, and offering settings there would lead nowhere. */}
          {consent?.isEnabled && (
            <>
              <span className="text-muted-foreground/40">·</span>
              <button onClick={consent.openPreferences} className="hover:text-foreground transition-colors underline-offset-2 hover:underline">
                Cookie settings
              </button>
            </>
          )}
        </div>
        {!isCompact && (
          <p className="max-w-2xl text-center text-[10px] leading-relaxed text-muted-foreground/60 mt-2 whitespace-pre-line">
            {legalContent?.footer || DEFAULT_FOOTER_TEXT}
          </p>
        )}
        <ReleaseVersion className={textSize} />
      </div>

      <LegalPolicyModal
        policy={openModal}
        onClose={() => setOpenModal(null)}
        legalContent={legalContent}
      />
    </>
  );
}
